import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

// Page publique d'activation restaurateur — accédée via le lien unique envoyé par l'admin.
// URL : /restaurateur/invitation?token=XXX
export default function RestaurateurInvitation() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { login } = useAuth();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null); // { email, restaurant_name, restaurant_logo, city, expires_at }
  const [error, setError] = useState(null);
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Lien d'invitation manquant ou incomplet.");
      setLoading(false);
      return;
    }
    api
      .get(`/restaurateur/invitation/${token}`)
      .then((r) => {
        setInfo(r.data);
        setLoading(false);
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail || "Ce lien n'est plus valide.";
        setError(msg);
        setLoading(false);
      });
  }, [token]);

  const canSubmit =
    pin.length === 4 && /^\d{4}$/.test(pin) && pin === confirmPin && !submitting;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) {
      if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
        toast.error("Le code d'accès doit contenir 4 chiffres.");
      } else if (pin !== confirmPin) {
        toast.error("Les deux codes ne correspondent pas.");
      }
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/restaurateur/invitation/${token}/accept`, { pin });
      login(data.token, data.user);
      toast.success("Compte activé ✓ Bienvenue sur Nold Go !");
      // Redirige vers l'espace restaurateur (déjà pré-configuré avec la vitrine)
      setTimeout(() => navigate("/restaurateur", { replace: true }), 400);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible d'activer le compte. Réessayez.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header brand Nold Go */}
        <div className="bg-[#F26419] rounded-t-2xl px-6 py-6 text-center text-white">
          <div className="font-head font-black text-2xl tracking-tight">Nold Go</div>
          <div className="text-xs opacity-90 mt-1">Livraison de repas · Bruxelles</div>
        </div>

        <div className="bg-white rounded-b-2xl border border-[#E2DFD8] border-t-0 p-6 shadow-lg">
          {loading && (
            <div data-testid="invite-loading" className="py-10 text-center text-[#75807A] text-sm">
              Chargement de votre invitation…
            </div>
          )}

          {!loading && error && (
            <div data-testid="invite-error" className="py-6 text-center">
              <div className="text-4xl mb-3">😕</div>
              <div className="font-head font-black text-lg text-[#1A1A1A] mb-2">Lien invalide</div>
              <div className="text-sm text-[#75807A] mb-5">{error}</div>
              <a
                href="mailto:contact@noldgo.com"
                className="inline-block text-sm text-[#F26419] font-bold underline"
                data-testid="invite-support-link"
              >
                Contacter le support Nold Go
              </a>
            </div>
          )}

          {!loading && !error && info && (
            <form onSubmit={submit} className="space-y-5">
              <div className="text-center">
                {info.restaurant_logo ? (
                  <img
                    src={info.restaurant_logo}
                    alt={info.restaurant_name}
                    className="w-20 h-20 rounded-2xl object-cover mx-auto border border-[#E2DFD8]"
                    data-testid="invite-resto-logo"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-2xl bg-[#F0EBE1] flex items-center justify-center text-3xl mx-auto">
                    🍽️
                  </div>
                )}
                <h1
                  data-testid="invite-title"
                  className="font-head font-black text-xl text-[#1A1A1A] mt-4"
                >
                  Bienvenue chez <span className="text-[#F26419]">{info.restaurant_name}</span>
                </h1>
                <p className="text-sm text-[#75807A] mt-1">
                  {info.city} · Compte : <b className="text-[#1A1A1A]">{info.email}</b>
                </p>
              </div>

              <div className="bg-[#F0EBE1]/50 rounded-xl p-4 text-sm text-[#3B423E] leading-relaxed">
                Créez votre <b>code d'accès à 4 chiffres</b>. Il vous permettra de vous connecter à
                votre espace restaurateur pour gérer votre menu, vos commandes et vos statistiques.
              </div>

              <div>
                <label className="block text-xs font-bold text-[#75807A] uppercase tracking-wide mb-1.5">
                  Code d'accès (4 chiffres)
                </label>
                <input
                  data-testid="invite-pin-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  autoComplete="new-password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="••••"
                  className="w-full h-14 rounded-xl border-2 border-[#E2DFD8] px-4 text-center text-2xl font-black tracking-[0.5em] focus:border-[#F26419] outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#75807A] uppercase tracking-wide mb-1.5">
                  Confirmez le code
                </label>
                <input
                  data-testid="invite-pin-confirm"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  autoComplete="new-password"
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="••••"
                  className="w-full h-14 rounded-xl border-2 border-[#E2DFD8] px-4 text-center text-2xl font-black tracking-[0.5em] focus:border-[#F26419] outline-none"
                />
              </div>

              <Button
                data-testid="invite-submit-btn"
                type="submit"
                disabled={!canSubmit}
                className="w-full h-12 rounded-xl bg-[#F26419] hover:bg-[#D94F2B] text-white font-black text-sm disabled:opacity-40"
              >
                {submitting ? "Activation en cours…" : "Activer mon compte restaurateur →"}
              </Button>

              <p className="text-[11px] text-[#75807A] text-center leading-relaxed">
                En activant, vous acceptez les conditions générales de Nold Go SRL. Vos données
                sont hébergées en Europe (Bruxelles).
              </p>
            </form>
          )}
        </div>

        <div className="text-center mt-4 text-[11px] text-[#75807A]">
          Nold Go SRL · Rue Jourdan 71 A0 · 1060 Saint-Gilles, Bruxelles
        </div>
      </div>
    </div>
  );
}
